import type { MembershipRole } from '@/api/types'

/**
 * Who sees what in the console (2026-10-BILLING-IN-APP BI4/BI5). The server enforces every rule
 * again; this decides what to draw and where a signed-in user lands.
 */

export const BILLING_MEMBER_LIMIT = 3

export interface Viewer {
  role: MembershipRole | undefined
  billing: boolean | undefined
}

/** ADMIN, the BILLING role, or a USER holding the flag. */
export function hasBilling(viewer: Viewer): boolean {
  return viewer.role === 'ADMIN' || viewer.role === 'BILLING' || viewer.billing === true
}

/** A BILLING-role member: money and read-only account pages, no technical reach at all. */
export function isBillingOnly(viewer: Viewer): boolean {
  return viewer.role === 'BILLING'
}

export type NavAccess = 'everyone' | 'technical' | 'admin' | 'billing' | 'users-read' | 'account'

/** Which sidebar entries a viewer gets. `technical` is everything a BILLING member cannot use. */
export function navAllowed(access: NavAccess | undefined, viewer: Viewer): boolean {
  switch (access) {
    case 'technical':
      return !isBillingOnly(viewer)
    case 'admin':
      return viewer.role === 'ADMIN'
    case 'billing':
      return hasBilling(viewer)
    case 'users-read':
      return viewer.role === 'ADMIN' || viewer.role === 'BILLING'
    case 'account':
    case 'everyone':
    case undefined:
      return true
  }
}

/** Where `/app` sends the viewer. */
export function landingPath(viewer: Viewer): string | null {
  return isBillingOnly(viewer) ? '/app/plan' : null
}

export const ROLE_OPTIONS: readonly MembershipRole[] = ['ADMIN', 'USER', 'BILLING']

export const ROLE_HELP: Record<MembershipRole, string> = {
  ADMIN: 'Reaches every zone in the account, and can create zones, grant access, manage users and billing.',
  USER: 'Reaches only the zones they have been granted, and cannot administer anything.',
  BILLING:
    'Billing only: plan, invoices, cards and the police balance, plus a read-only view of users and usage. No zones, no agents. Free: never a seat.',
}

/** Billing access for a USER is a flag; ADMIN and BILLING hold it by role. */
export function offersBillingFlag(role: MembershipRole): boolean {
  return role === 'USER'
}

export function billingMembers(users: readonly { role: MembershipRole; user_state: string }[]): number {
  return users.filter((user) => user.role === 'BILLING' && user.user_state === 'ACTIVE').length
}

export function billingSlotsFull(users: readonly { role: MembershipRole; user_state: string }[]): boolean {
  return billingMembers(users) >= BILLING_MEMBER_LIMIT
}

/** Members that count against the plan: everyone active except BILLING (BI6). */
export function seatMembers(users: readonly { role: MembershipRole; user_state: string }[]): number {
  return users.filter((user) => user.role !== 'BILLING' && user.user_state === 'ACTIVE').length
}

const USER_ERRORS: Record<string, string> = {
  billing_member_limit: `An account can have at most ${BILLING_MEMBER_LIMIT} billing members.`,
  billing_has_no_zones: 'A billing member has no zone access. Make them a USER with billing access if they also need zones.',
}

export function userErrorText(code: string | undefined, message: string): string {
  return (code !== undefined ? USER_ERRORS[code] : undefined) ?? message
}
